/*
  cart-system.js

  Single-source-of-truth cart store + <add-to-cart-button> custom element.
  Loaded with `defer` from layout/theme.liquid. Previously inline in <head>;
  extracted here for code organization once the architectural rebuild
  eliminated the pre-hydration race that originally required inline loading.

  This file defines:
    - window.cart       — the cart store (get/set/subscribe/refresh/add/update/remove)
    - window.cartStore  — back-compat alias for window.cart
    - window.atc        — back-compat wrapper for window.cart.add
    - global submit interceptor for any <form data-product-form> that escapes
    - <add-to-cart-button> custom element

  Must be loaded BEFORE theme.js (since theme.js's cart drawer uses window.cart).
  Place the <script src="cart-system.js" defer> tag earlier in <head> than the
  theme.js tag — defer scripts execute in document order.
*/

    /*
      window.cart — single source of truth for cart state.

      Design principle: every mutation (add / update / remove) refreshes the
      cart from /cart.js so the in-memory state always reflects what the
      server actually has. The drawer and any other view never makes a
      decision from cached state — it always renders from window.cart.get()
      which is guaranteed fresh as of the last mutation.

      This is loaded inline in <head> so it is available before any markup
      paints. No defer dependency. The legacy form/submit handler still
      below catches any <form action="/cart/add"> that escapes (e.g. before
      Alpine hydrates on a slow connection).
    */
    (function () {
      var subscribers = [];
      var current = null;
      var pending = Promise.resolve();

      function emit(eventName, detail) {
        document.dispatchEvent(new CustomEvent(eventName, {
          detail: detail || {},
          bubbles: true
        }));
      }

      // Sections we request alongside every cart mutation so the drawer
      // doesn't need a separate /?section_id fetch. Shopify's Section
      // Rendering API returns the rendered HTML in the same response
      // (response.sections[name]). Saves ~150ms per cart op.
      var DEFAULT_SECTIONS = 'api-cart-items';

      // Cache for the most recent sections map, so views can read it
      // without refetching when they know the store is fresh (e.g. the
      // drawer opening immediately after an ATC).
      var lastSections = {};

      function notify(sections) {
        var payload = sections || {};
        lastSections = payload;
        subscribers.forEach(function (cb) {
          try { cb(current, payload); } catch (e) { console.error('[cart subscriber]', e); }
        });
        emit('cart:updated', { cart: current, sections: payload });
        // Back-compat for existing theme.js listeners.
        emit('theme:cart:change', { cart: current });
      }

      function jsonFetch(url, options) {
        options = options || {};
        options.headers = Object.assign({
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }, options.headers || {});
        return fetch(url, options).then(function (r) {
          if (!r.ok) {
            return r.json().then(function (err) {
              var e = new Error(err.description || err.message || 'Cart request failed');
              e.data = err;
              e.status = r.status;
              throw e;
            }, function () {
              throw new Error('Cart request failed: ' + r.status);
            });
          }
          return r.json();
        });
      }

      // Extract sections from a cart endpoint response and return a clean
      // copy of the cart without the sections key. Handles both response
      // shapes: /cart.js + /cart/change.js return the cart at the top
      // level with sections nested; /cart/add.js returns the added item
      // at the top level with sections nested.
      function splitSections(response) {
        if (!response || typeof response !== 'object') {
          return { data: response, sections: {} };
        }
        var sections = response.sections || {};
        // Don't mutate caller's object — shallow clone without 'sections'.
        var clean = {};
        Object.keys(response).forEach(function (k) {
          if (k !== 'sections') clean[k] = response[k];
        });
        return { data: clean, sections: sections };
      }

      function refreshAndNotify() {
        return jsonFetch('/cart.js?sections=' + DEFAULT_SECTIONS).then(function (response) {
          var split = splitSections(response);
          current = split.data;
          notify(split.sections);
          return current;
        });
      }

      function enqueue(fn) {
        var run = pending.then(fn);
        // Catch so a failed call doesn't poison the chain for the next one.
        pending = run.catch(function () {});
        return run;
      }

      function bodyFromInput(input, properties) {
        if (input instanceof FormData) {
          return new URLSearchParams(input).toString();
        }
        if (typeof input === 'object' && input !== null && !Array.isArray(input)) {
          return new URLSearchParams(input).toString();
        }
        var params = { id: input, quantity: 1 };
        if (properties && typeof properties === 'object') {
          Object.keys(properties).forEach(function (k) {
            params['properties[' + k + ']'] = properties[k];
          });
        }
        return new URLSearchParams(params).toString();
      }

      // Total units of a variant currently in a cart object.
      function variantQty(cart, variantId) {
        if (!cart || !cart.items || !variantId) return 0;
        return cart.items.reduce(function (sum, line) {
          return sum + (String(line.variant_id || line.id) === String(variantId) ? line.quantity : 0);
        }, 0);
      }

      function postAdd(body) {
        return jsonFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body
        });
      }

      /*
        POST /cart/add.js with one-shot recovery for network-class
        failures (flaky mobile connections are the common case).

        - 4xx responses (sold out, invalid variant, quantity rules) are
          surfaced immediately — retrying is pointless and would hide
          the real message from the customer.
        - For network errors we first VERIFY against /cart.js whether
          the original POST actually landed: the request can succeed
          while only the response is lost. Blindly retrying in that
          case would double-add the item. Only if the cart shows no
          new quantity for the variant do we retry the POST, once.
        - Verification needs a trusted baseline (`current`, settled
          because the queue serializes mutations). If we have no
          baseline we can't verify, so we don't retry — never risk a
          double-add for a retry.
      */
      var RETRY_DELAY_MS = 400;
      function postAddWithRecovery(body) {
        var variantId = new URLSearchParams(body).get('id');
        var baseline = current;
        var baselineQty = variantQty(baseline, variantId);
        return postAdd(body).catch(function (err) {
          var isClientError = err && err.status >= 400 && err.status < 500;
          if (isClientError || !variantId || !baseline) throw err;
          return jsonFetch('/cart.js').then(function (cart) {
            if (variantQty(cart, variantId) > baselineQty) {
              // First POST landed; only the response was lost.
              // Synthesize the added-item payload from the cart line.
              var line = cart.items.filter(function (l) {
                return String(l.variant_id || l.id) === String(variantId);
              })[0];
              return line || {};
            }
            return new Promise(function (resolve) {
              setTimeout(resolve, RETRY_DELAY_MS);
            }).then(function () { return postAdd(body); });
          }, function () { throw err; });
        });
      }

      // Minimal global toast for cart errors on surfaces that have no
      // local [data-error-display]. Inline styles so it works without
      // theme CSS changes.
      function showCartToast(message) {
        var existing = document.querySelector('.cart-system-toast');
        if (existing) existing.remove();
        var el = document.createElement('div');
        el.className = 'cart-system-toast';
        el.setAttribute('role', 'alert');
        el.textContent = message;
        el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);' +
          'background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:6px;' +
          'z-index:10000;max-width:90vw;font-size:14px;text-align:center;' +
          'box-shadow:0 4px 12px rgba(0,0,0,.25)';
        document.body.appendChild(el);
        setTimeout(function () { el.remove(); }, 5000);
      }

      window.cart = {
        get: function () { return current; },

        // Most recently received sections map (e.g. {'api-cart-items': '<...>'}).
        // Views can use this to render from cached HTML without a fetch
        // when they know the store is fresh.
        getSections: function () { return lastSections; },

        subscribe: function (cb) {
          subscribers.push(cb);
          if (current) { try { cb(current); } catch (e) { console.error(e); } }
          return function () {
            subscribers = subscribers.filter(function (s) { return s !== cb; });
          };
        },

        // Re-fetch /cart.js and notify subscribers. Use whenever you need to
        // be sure the in-memory cart matches the server.
        refresh: function () {
          return enqueue(refreshAndNotify);
        },

        // Add an item. Accepts a FormData, a plain object, or a variant id
        // with optional properties. Always refreshes from /cart.js after
        // the POST so server-side cart hooks (e.g. Kaching gift items) are
        // included in the cart we hand back to subscribers.
        //
        // /cart/add.js returns the added item but NOT the full cart, so
        // we still need the /cart.js refresh. We request sections on the
        // refresh so the drawer can render without a third fetch.
        add: function (input, properties) {
          var body = bodyFromInput(input, properties);
          emit('cart:adding');
          return enqueue(function () {
            return postAddWithRecovery(body).then(function (addedItem) {
              return refreshAndNotify().then(function (cart) {
                emit('cart:added', { addedItem: addedItem, cart: cart });
                return { addedItem: addedItem, cart: cart };
              });
            });
          }).catch(function (err) {
            emit('cart:error', { error: err });
            throw err;
          });
        },

        // Update a line item quantity (key from cart.items[i].key).
        // /cart/change.js returns the full cart object, and with the
        // sections parameter it also returns rendered section HTML in the
        // same response. One round trip for the entire operation.
        update: function (key, quantity) {
          return enqueue(function () {
            return jsonFetch('/cart/change.js?sections=' + DEFAULT_SECTIONS, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: key, quantity: quantity })
            }).then(function (response) {
              var split = splitSections(response);
              current = split.data;
              notify(split.sections);
              return current;
            });
          }).catch(function (err) {
            emit('cart:error', { error: err });
            throw err;
          });
        },

        remove: function (key) {
          return this.update(key, 0);
        }
      };

      // Back-compat aliases for code written against the previous patch.
      window.cartStore = window.cart;
      window.atc = function (input, properties) {
        return window.cart.add(input, properties).then(function (result) {
          // Legacy callers expect { data: variantResponse }.
          return { data: result.addedItem, cart: result.cart };
        });
      };

      // Initial fetch + bfcache restoration.
      function bootstrap() { window.cart.refresh(); }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootstrap);
      } else {
        bootstrap();
      }
      window.addEventListener('pageshow', function (e) {
        if (e.persisted) bootstrap();
      });

      // Safety-net form interceptor. Catches any product form that gets
      // submitted natively (e.g. before a custom element wires up). Routes
      // through window.cart.add so the same cart-refresh logic runs.
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.matches) return;
        var action = form.getAttribute('action') || '';
        var isAtcForm =
          form.hasAttribute('data-product-form') ||
          /\/cart\/add(\?.*)?$/.test(action);
        if (!isAtcForm) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.cart.add(new FormData(form)).catch(function (err) {
          // By the time we get here, postAddWithRecovery has already
          // verified the add didn't land and retried once — this is a
          // persistent failure, so tell the customer. Prefer the form's
          // local error display; fall back to a global toast so the
          // error is visible from any surface.
          console.error('[cart form interceptor]', err);
          var message = (err && err.data && (err.data.description || err.data.message))
                     || (err && err.message)
                     || 'Could not add to cart. Please try again.';
          var display = form.querySelector('[data-error-display]');
          if (display) {
            display.innerHTML = '<div class="errors">' + message + '</div>';
            display.style.display = 'block';
            setTimeout(function () { display.style.display = 'none'; }, 5000);
          } else {
            showCartToast(message);
          }
        });
      }, true);

      /*
        <add-to-cart-button> custom element.

        Self-contained Web Component used by the main product page (and
        any other surface that opts into the new pattern). Reads form
        data from its enclosing <form>, calls window.cart.add, manages
        loading/success/error states on the inner button.

        Defined inline so it's registered before any markup parses —
        avoids the pre-hydration race that plagued the Alpine-based
        button (clicks before theme.js loaded fell through to a native
        form submit and dropped Kaching gifts).

        Usage in Liquid:
          <add-to-cart-button>
            <div data-error-display></div>
            <button type="button" data-atc-trigger class="btn">Add to Cart</button>
          </add-to-cart-button>

        The custom element finds the button via [data-atc-trigger] (or
        the first <button> child), wires up click -> window.cart.add,
        and toggles 'loading' / 'has-success' classes on the button so
        existing theme CSS continues to apply unchanged.
      */
      if (!customElements.get('add-to-cart-button')) {
        class AddToCartButtonEl extends HTMLElement {
          connectedCallback() {
            // IMPORTANT: connectedCallback fires when the opening tag is
            // parsed, BEFORE children exist. We cannot querySelector for
            // the button here. Instead we attach a click listener to the
            // custom element itself and use event delegation — clicks on
            // any child button bubble up and we find the trigger then.
            if (this._wired) return;
            this._wired = true;
            this.successDurationMs = parseInt(this.getAttribute('data-success-duration'), 10) || 1000;
            this.errorDurationMs = parseInt(this.getAttribute('data-error-duration'), 10) || 5000;
            this.addEventListener('click', this._onClick.bind(this));
          }

          _onClick(e) {
            // Find the trigger button — prefer the explicit data-atc-trigger
            // marker, fall back to any button descendant.
            var trigger = e.target.closest('[data-atc-trigger]');
            if (!trigger) {
              var maybeBtn = e.target.closest('button');
              if (maybeBtn && this.contains(maybeBtn)) trigger = maybeBtn;
            }
            if (!trigger || !this.contains(trigger)) return;

            e.preventDefault();
            if (trigger.disabled || this.classList.contains('is-loading')) return;

            // Cache references on first click — children are guaranteed
            // parsed by the time the user can click anything.
            this.button = trigger;
            if (!this.errorDisplay) {
              this.errorDisplay = this.querySelector('[data-error-display]');
            }

            // Find the form whether it's an ancestor (main product page:
            // <form> wraps <add-to-cart-button>) OR a descendant (upsell:
            // <add-to-cart-button> wraps <form>). trigger.closest covers
            // the ancestor case; this.querySelector covers the descendant.
            var form = trigger.closest('form') || this.querySelector('form');
            var formData;
            if (form) {
              formData = new FormData(form);
            } else if (this.hasAttribute('data-variant-id')) {
              // Form-less mode: rely on attributes.
              formData = new FormData();
              formData.append('id', this.getAttribute('data-variant-id'));
              formData.append('quantity', this.getAttribute('data-quantity') || '1');
              var propsRaw = this.getAttribute('data-properties');
              if (propsRaw) {
                try {
                  var props = JSON.parse(propsRaw);
                  Object.keys(props).forEach(function (k) {
                    formData.append('properties[' + k + ']', props[k]);
                  });
                } catch (err) { /* ignore malformed JSON */ }
              }
            } else {
              console.error('[add-to-cart-button] no <form> ancestor and no data-variant-id');
              return;
            }

            if (!window.cart || typeof window.cart.add !== 'function') {
              console.error('[add-to-cart-button] window.cart not available');
              return;
            }

            this._setLoading();
            var self = this;
            window.cart.add(formData)
              .then(function (result) {
                self._setSuccess();
                self._openDrawer();
                return result;
              })
              .catch(function (err) {
                self._setError(err);
              });
          }

          _setLoading() {
            this.button.classList.add('loading');
            this.button.classList.remove('has-success');
            this.classList.add('is-loading');
            this.classList.remove('is-success', 'is-error');
          }

          _setSuccess() {
            var self = this;
            this.button.classList.remove('loading');
            this.button.classList.add('has-success');
            this.classList.remove('is-loading');
            this.classList.add('is-success');
            setTimeout(function () {
              self.button.classList.remove('has-success');
              self.classList.remove('is-success');
            }, this.successDurationMs);
          }

          _setError(err) {
            var self = this;
            this.button.classList.remove('loading', 'has-success');
            this.classList.remove('is-loading', 'is-success');
            this.classList.add('is-error');
            var message = (err && err.data && (err.data.description || err.data.message))
                       || (err && err.message)
                       || 'Could not add to cart. Please try again.';
            if (this.errorDisplay) {
              this.errorDisplay.innerHTML = '<div class="errors">' + message + '</div>';
              this.errorDisplay.style.display = 'block';
            }
            setTimeout(function () {
              if (self.errorDisplay) self.errorDisplay.style.display = 'none';
              self.classList.remove('is-error');
            }, this.errorDurationMs);
            console.error('[add-to-cart-button]', err);
          }

          _openDrawer() {
            var drawer = document.querySelector('[data-drawer="drawer-cart"]');
            if (drawer) {
              drawer.dispatchEvent(new CustomEvent('theme:drawer:open', {
                bubbles: true
              }));
            }
          }
        }
        customElements.define('add-to-cart-button', AddToCartButtonEl);
      }
    })();
